import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CalendarClock,
  CheckSquare,
  DollarSign,
  Inbox,
  MapPin,
  RefreshCw,
} from 'lucide-react';
import type { DashboardToday, DashboardTodayEvent } from '@peles/shared';
import { formatMoney } from '../events/workspace/EventOverviewTab';
import { isAllDayEvent } from '../../utils/eventDates';

function greeting(now: Date): string {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole calendar days from today to the date (0 = today, negative = past). */
export function daysFromToday(date: Date, now: Date): number {
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((day(date) - day(now)) / DAY_MS);
}

/** "6:00 PM" for today's start, "Since Tue" for one underway, "All day" for an all-day event. */
function whenToday(e: DashboardTodayEvent, now: Date): string {
  const start = new Date(e.startAt);
  if (daysFromToday(start, now) < 0) {
    return `Since ${start.toLocaleDateString('en-US', { weekday: 'short' })}`;
  }
  if (isAllDayEvent(start, e.endAt)) return 'All day';
  return start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function Attention({
  to,
  icon,
  tone,
  children,
}: {
  to: string;
  icon: ReactNode;
  tone: 'red' | 'amber' | 'blue';
  children: ReactNode;
}) {
  const tones = {
    red: 'bg-red-50 text-red-800 border-red-100',
    amber: 'bg-amber-50 text-amber-900 border-amber-100',
    blue: 'bg-primary-50 text-primary-800 border-primary-100',
  };
  return (
    <Link
      to={to}
      className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm font-medium hover:brightness-95 ${tones[tone]}`}
    >
      {icon}
      {children}
    </Link>
  );
}

function attentionItems(t: DashboardToday): ReactNode[] {
  const items: ReactNode[] = [];
  if (t.tasksOverdue > 0) {
    items.push(
      <Attention
        key="overdue"
        to="/tasks?due=overdue"
        tone="red"
        icon={<AlertTriangle size={16} />}
      >
        {t.tasksOverdue} {t.tasksOverdue === 1 ? 'task' : 'tasks'} overdue
      </Attention>,
    );
  }
  if (t.tasksDueToday > 0) {
    items.push(
      <Attention key="today" to="/tasks?due=today" tone="amber" icon={<CheckSquare size={16} />}>
        {t.tasksDueToday} due today
      </Attention>,
    );
  }
  if (t.overduePayments.count > 0) {
    items.push(
      <Attention key="payments" to="/expenses" tone="red" icon={<DollarSign size={16} />}>
        {t.overduePayments.count} overdue {t.overduePayments.count === 1 ? 'payment' : 'payments'} ·{' '}
        {formatMoney(t.overduePayments.total)}
      </Attention>,
    );
  }
  if (t.newInquiries > 0) {
    items.push(
      <Attention
        key="inquiries"
        to="/communications/inquiries"
        tone="blue"
        icon={<Inbox size={16} />}
      >
        {t.newInquiries} {t.newInquiries === 1 ? 'enquiry' : 'enquiries'} awaiting a reply
      </Attention>,
    );
  }
  return items;
}

function TodayBody({ today, now }: { today: DashboardToday; now: Date }) {
  const attention = attentionItems(today);
  const nextDays = today.next ? daysFromToday(new Date(today.next.startAt), now) : 0;
  return (
    <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
          {today.today.length > 0 ? 'Happening today' : 'Next up'}
        </h2>
        {today.today.length > 0 ? (
          <ul className="space-y-2">
            {today.today.map((e) => (
              <li key={e.id}>
                <Link
                  to={`/events/${e.id}`}
                  className="flex items-center gap-3 rounded-xl bg-primary-600 text-white px-4 py-3 hover:bg-primary-700"
                >
                  <span className="text-sm font-semibold tabular-nums w-20 shrink-0">
                    {whenToday(e, now)}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-semibold truncate">{e.name}</span>
                    {e.venue && (
                      <span className="flex items-center gap-1 text-xs text-primary-100 truncate">
                        <MapPin size={12} />
                        {e.venue}
                      </span>
                    )}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : today.next ? (
          <Link
            to={`/events/${today.next.id}`}
            className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 hover:border-primary-300"
          >
            <CalendarClock size={20} className="text-primary-600 shrink-0" />
            <span className="min-w-0">
              <span className="block font-semibold text-gray-900 truncate">{today.next.name}</span>
              <span className="block text-xs text-gray-500">
                {nextDays <= 1 ? 'Tomorrow' : `In ${nextDays} days`}
                {today.laterThisWeek > 1 && ` · ${today.laterThisWeek} events in the next 7 days`}
              </span>
            </span>
          </Link>
        ) : (
          <p className="text-sm text-gray-500">No upcoming events.</p>
        )}
      </div>

      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
          Needs attention
        </h2>
        {attention.length > 0 ? (
          <div className="flex flex-col gap-2">{attention}</div>
        ) : (
          <p className="text-sm text-gray-500">Nothing overdue. A clear run.</p>
        )}
      </div>
    </div>
  );
}

/**
 * The top of the producer's dashboard: today first. What is happening today
 * (or what is next), then only the things that need doing — overdue and
 * due-today tasks, overdue vendor payments, enquiries awaiting a reply. The
 * figures come from `GET /dashboard/today`, counted over every record; the
 * stats endpoint's lists are previews, and totals read off them understate.
 */
export function TodayFocus({
  today,
  isLoading = false,
  isError = false,
  onRetry,
  now = new Date(),
}: {
  today: DashboardToday | undefined;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  now?: Date;
}) {
  const dateLabel = now.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  return (
    <section
      aria-label="Today"
      className="mb-6 rounded-2xl bg-white border border-gray-200 p-5 md:p-6"
    >
      <p className="text-sm font-medium text-primary-700">{dateLabel}</p>
      <h1 className="text-2xl font-bold text-gray-900 mt-0.5">{greeting(now)}</h1>

      {today ? (
        <TodayBody today={today} now={now} />
      ) : isError ? (
        // Never an empty day in place of one that failed to load: "nothing
        // overdue" would be a claim this page cannot make.
        <div
          role="alert"
          className="mt-5 flex flex-wrap items-center gap-3 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-900"
        >
          <AlertTriangle size={16} />
          Today&apos;s events and tasks could not be loaded.
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-1.5 font-semibold underline-offset-2 hover:underline"
            >
              <RefreshCw size={14} />
              Try again
            </button>
          )}
        </div>
      ) : (
        isLoading && (
          <div className="mt-5 grid gap-5 lg:grid-cols-2 animate-pulse" aria-busy>
            <div className="h-16 rounded-xl bg-gray-100" />
            <div className="h-16 rounded-xl bg-gray-100" />
          </div>
        )
      )}
    </section>
  );
}
